/** Form Inventory workflow — validation and save (skeleton register) */

import {
  MESSAGES,
  SELECTABLE_CONTROL_TYPES,
  VALIDATION_MESSAGES,
  createEmptyForm,
  formatFieldId,
  formatFormId,
  getFieldKey,
  getFormKey,
  nextFieldIdNumber,
  nextFormIdNumber,
} from './formInventoryConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';
import { CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';

const nowIso = () => new Date().toISOString();

export const DEFAULT_ACTOR = 'Development Team Member — DEV-000014';

const normalizeList = (values = []) =>
  values.map((v) => String(v || '').trim()).filter(Boolean);

export const cloneFormRecord = (form) => JSON.parse(JSON.stringify(form || createEmptyForm()));

export const controlNeedsSelectableSource = (controlType) =>
  SELECTABLE_CONTROL_TYPES.includes(controlType);

const hasDuplicateStrings = (values = []) => {
  const lower = values.map((v) => v.toLowerCase());
  return new Set(lower).size !== lower.length;
};

export const validateFormField = (
  field,
  formModes = [],
  siblingFields = [],
  fieldIndex = 0,
) => {
  const errors = {};
  const label = String(field.fieldLabel || '').trim();
  const fieldKey = getFieldKey(field);
  const modes = field.availableModes || [];
  const order = Number(field.displayOrder);
  const needsSelectable = controlNeedsSelectableSource(field.controlType);

  if (!label) errors.fieldLabel = VALIDATION_MESSAGES.fieldLabelMissing;
  if (!fieldKey) errors.fieldKey = VALIDATION_MESSAGES.fieldKeyMissing;
  else {
    const duplicate = siblingFields.some(
      (other, idx) =>
        idx !== fieldIndex && getFieldKey(other).toLowerCase() === fieldKey.toLowerCase(),
    );
    if (duplicate) errors.fieldKey = VALIDATION_MESSAGES.duplicateFieldKey;
  }

  if (!field.controlType) errors.controlType = VALIDATION_MESSAGES.controlTypeMissing;

  if (!Number.isInteger(order) || order < 1) {
    errors.displayOrder = VALIDATION_MESSAGES.displayOrderInvalid;
  } else {
    const duplicateOrder = siblingFields.some((other, idx) => {
      if (idx === fieldIndex) return false;
      const otherModes = other.availableModes || [];
      const sharedMode = modes.some((m) => otherModes.includes(m));
      return sharedMode && Number(other.displayOrder) === order;
    });
    if (duplicateOrder) errors.displayOrder = VALIDATION_MESSAGES.displayOrderInvalid;
  }

  if (!modes.length) errors.availableModes = VALIDATION_MESSAGES.availableModesMissing;
  else if (modes.some((m) => !formModes.includes(m))) {
    errors.availableModes = VALIDATION_MESSAGES.unsupportedMode;
  }

  if (needsSelectable) {
    if (!field.selectableValueSource) {
      errors.selectableValueSource = VALIDATION_MESSAGES.selectableValueSourceMissing;
    } else if (field.selectableValueSource === 'Fixed Form Values') {
      if (String(field.savedSourceRelationship || '').trim()) {
        errors.selectableValueSource = VALIDATION_MESSAGES.bothValueSources;
      }
      if (!normalizeList(field.fixedFormValues).length) {
        errors.fixedFormValues = VALIDATION_MESSAGES.fixedFormValuesMissing;
      } else if (hasDuplicateStrings(normalizeList(field.fixedFormValues))) {
        errors.fixedFormValues = 'Duplicate fixed values within the same field are blocked.';
      }
    } else if (field.selectableValueSource === 'Saved Source Records') {
      if (normalizeList(field.fixedFormValues).length) {
        errors.selectableValueSource = VALIDATION_MESSAGES.bothValueSources;
      }
      if (!String(field.savedSourceRelationship || '').trim()) {
        errors.savedSourceRelationship = VALIDATION_MESSAGES.savedSourceMissing;
      }
    }
  } else if (
    field.selectableValueSource ||
    normalizeList(field.fixedFormValues).length ||
    String(field.savedSourceRelationship || '').trim()
  ) {
    errors.selectableValueSource = VALIDATION_MESSAGES.choiceSourceOnNonSelection;
  }

  if (!String(field.sampleValue || '').trim()) {
    errors.sampleValue = VALIDATION_MESSAGES.sampleValueMissing;
  }
  if (!String(field.dataSource || '').trim()) {
    errors.dataSource = VALIDATION_MESSAGES.dataSourceMissing;
  }
  if (!String(field.destination || field.recordDestination || '').trim()) {
    errors.destination = VALIDATION_MESSAGES.destinationMissing;
  }

  return errors;
};

export const validateFormRecord = (form, existingForms = [], options = {}) => {
  const { excludeId = null } = options;
  const errors = {};
  const fields = form.fields || [];
  const entryPoints = normalizeList(form.entryPoints);
  const modes = normalizeList(form.modes);
  const name = String(form.formName || '').trim();
  const purpose = String(form.purpose || '').trim();
  const formKey = getFormKey(form);
  const compareId = excludeId || form.id;

  if (!formKey) errors.formKey = VALIDATION_MESSAGES.formKeyMissing;
  else {
    const duplicateKey = existingForms.some(
      (other) => getFormKey(other).toLowerCase() === formKey.toLowerCase() && other.id !== compareId,
    );
    if (duplicateKey) errors.formKey = VALIDATION_MESSAGES.duplicateFormKey;
  }

  if (!name) errors.formName = VALIDATION_MESSAGES.formNameMissing;
  else {
    const duplicateName = existingForms.some(
      (other) =>
        String(other.formName || '').trim().toLowerCase() === name.toLowerCase() &&
        other.id !== compareId,
    );
    if (duplicateName) errors.formName = VALIDATION_MESSAGES.duplicateFormName;
  }

  if (!purpose) errors.purpose = VALIDATION_MESSAGES.purposeMissing;
  if (!form.category) errors.category = VALIDATION_MESSAGES.formCategoryMissing;
  if (!form.owningModule) errors.owningModule = VALIDATION_MESSAGES.owningModuleMissing;
  if (!(form.usedBy || []).length) errors.usedBy = VALIDATION_MESSAGES.usedByMissing;

  if (!entryPoints.length) errors.entryPoints = VALIDATION_MESSAGES.entryPointMissing;
  else if (hasDuplicateStrings(entryPoints)) {
    errors.entryPoints = VALIDATION_MESSAGES.duplicateEntryPoints;
  }

  if (!modes.length) errors.modes = VALIDATION_MESSAGES.modesMissing;
  else if (hasDuplicateStrings(modes)) {
    errors.modes = VALIDATION_MESSAGES.duplicateModes;
  }

  if (!fields.length) errors.fields = VALIDATION_MESSAGES.noFormFields;

  const fieldErrors = fields.map((field, index) =>
    validateFormField(field, modes, fields, index),
  );
  if (fieldErrors.some((e) => Object.keys(e).length)) {
    errors.fieldErrors = fieldErrors;
  }

  return errors;
};

export const hasValidationErrors = (errors = {}) => {
  if (errors.fieldErrors?.some((e) => Object.keys(e || {}).length)) return true;
  return Object.keys(errors).some((key) => key !== 'fieldErrors');
};

const normalizeFieldForSave = (field, index) => {
  const needsSelectable = controlNeedsSelectableSource(field.controlType);
  const selectableValueSource = needsSelectable ? field.selectableValueSource || '' : '';
  return {
    id: field.id || '',
    fieldLabel: String(field.fieldLabel || '').trim(),
    fieldKey: getFieldKey(field),
    controlType: field.controlType || '',
    displayOrder: Number(field.displayOrder) > 0 ? Number(field.displayOrder) : index + 1,
    availableModes: [...(field.availableModes || [])],
    selectableValueSource,
    fixedFormValues:
      selectableValueSource === 'Fixed Form Values' ? normalizeList(field.fixedFormValues) : [],
    savedSourceRelationship:
      selectableValueSource === 'Saved Source Records'
        ? String(field.savedSourceRelationship || '').trim()
        : '',
    placeholder: String(field.placeholder || '').trim(),
    sampleValue: String(field.sampleValue || '').trim(),
    dataSource: String(field.dataSource || '').trim(),
    destination: String(field.destination || field.recordDestination || '').trim(),
  };
};

const recordsEqualForSave = (a, b) => {
  const stripMeta = (form) => {
    const clone = cloneFormRecord(form);
    delete clone.lastUpdated;
    delete clone.lastUpdatedBy;
    delete clone.dateAdded;
    delete clone.addedBy;
    return JSON.stringify(clone);
  };
  return stripMeta(a) === stripMeta(b);
};

export const mergeFormsAfterSave = (forms, form, { isNew = false, actor = DEFAULT_ACTOR } = {}) => {
  const stamp = nowIso();
  let nextForm = cloneFormRecord(form);
  nextForm.formKey = getFormKey(nextForm);
  nextForm.formName = String(nextForm.formName || '').trim();
  nextForm.purpose = String(nextForm.purpose || '').trim();
  nextForm.entryPoints = normalizeList(nextForm.entryPoints);
  nextForm.modes = normalizeList(nextForm.modes);
  nextForm.usedBy = [...(nextForm.usedBy || [])];
  nextForm.fields = (nextForm.fields || []).map((field, index) => normalizeFieldForSave(field, index));

  if (isNew) {
    let fieldNum = nextFieldIdNumber(forms);
    nextForm.id = formatFormId(nextFormIdNumber(forms));
    nextForm.dateAdded = stamp;
    nextForm.addedBy = actor;
    nextForm.lastUpdated = stamp;
    nextForm.lastUpdatedBy = actor;
    nextForm.fields = nextForm.fields.map((field) => {
      if (field.id) return field;
      const assigned = { ...field, id: formatFieldId(fieldNum) };
      fieldNum += 1;
      return assigned;
    });
    try {
      emitRuleSaveHistory({
        isNew: true,
        previous: null,
        next: {
          ...nextForm,
          fieldCount: (nextForm.fields || []).length,
        },
        module: 'Form Inventory',
        sourceModuleRef: 'form_inventory',
        changedItem: nextForm.formName || nextForm.id,
        itemId: nextForm.id,
        changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
        actor,
        stamp,
        compareKeys: ['formName', 'purpose', 'entryPoints', 'modes', 'fieldCount'],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
    return {
      forms: [...forms, nextForm],
      form: nextForm,
      message: MESSAGES.saved,
      changed: true,
    };
  }

  const existing = forms.find((f) => f.id === nextForm.id);
  if (!existing) {
    return { forms, form: nextForm, message: MESSAGES.updated, changed: false };
  }

  // Permanent IDs and saved keys remain read-only
  nextForm.id = existing.id;
  nextForm.formKey = getFormKey(existing);
  nextForm.dateAdded = existing.dateAdded;
  nextForm.addedBy = existing.addedBy;
  nextForm.fields = nextForm.fields.map((field) => {
    if (!field.id) return field;
    const prior = (existing.fields || []).find((f) => f.id === field.id);
    if (prior) {
      return { ...field, id: prior.id, fieldKey: getFieldKey(prior) };
    }
    return field;
  });

  let fieldNum = nextFieldIdNumber(forms);
  nextForm.fields = nextForm.fields.map((field) => {
    if (field.id) return field;
    const assigned = { ...field, id: formatFieldId(fieldNum) };
    fieldNum += 1;
    return assigned;
  });

  const changed = !recordsEqualForSave(
    { ...existing, fields: existing.fields },
    { ...nextForm, lastUpdated: existing.lastUpdated, lastUpdatedBy: existing.lastUpdatedBy },
  );

  if (changed) {
    nextForm.lastUpdated = stamp;
    nextForm.lastUpdatedBy = actor;
    try {
      emitRuleSaveHistory({
        isNew: false,
        previous: {
          ...existing,
          fieldCount: (existing.fields || []).length,
        },
        next: {
          ...nextForm,
          fieldCount: (nextForm.fields || []).length,
        },
        module: 'Form Inventory',
        sourceModuleRef: 'form_inventory',
        changedItem: nextForm.formName || nextForm.id,
        itemId: nextForm.id,
        changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
        actor,
        stamp,
        compareKeys: ['formName', 'purpose', 'entryPoints', 'modes', 'fieldCount'],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  } else {
    nextForm.lastUpdated = existing.lastUpdated;
    nextForm.lastUpdatedBy = existing.lastUpdatedBy;
  }

  return {
    forms: forms.map((f) => (f.id === nextForm.id ? nextForm : f)),
    form: nextForm,
    message: changed ? MESSAGES.updated : MESSAGES.updated,
    changed,
  };
};
